function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useMemo, useCallback, useState } from 'react';
import costFragmentsService from '../../services/claim/cost_fragments.service';
import { CardErrorMessage, Loading, DataGridTable } from '@rd-web-markets/shared/dist/util';
import { staffingCostFragmentTableSettings as employeeTableSettings } from '../staffingCosts/costFragmentTableSettings';
import { staffingCostFragmentTableSettings as subcontractorTableSettings } from '../subcontractors/costFragmentTableSettings';
import { staffingCostFragmentTableSettings as externalWorkerTableSettings } from '../externalWorkers/costFragmentTableSettings';
import { staffingCostFragmentTableSettings as consumableTableSettings } from '../consumables/costFragmentTableSettings';
import { staffingCostFragmentTableSettings as softwareConsumableTableSettings } from '../ITConsumables/costFragmentTableSettings';
import { staffingCostFragmentTableSettings as cloudComputingTableSettings } from '../cloudComputings/costFragmentTableSettings';
import { staffingCostFragmentTableSettings as dataLicenseCostTableSettings } from '../dataLicencesCosts/costFragmentTableSettings';
import { staffingCostFragmentTableSettings as independentRndContributionCostTableSettings } from '../independentRndContributions/costFragmentTableSettings';
import { staffingCostFragmentTableSettings as paymentsToClinicalTrialCostTableSettings } from '../paymentsToClinicalTrials/costFragmentTableSettings';
import { CostFragmentCell } from './CostFragmentCell';
import { useTotalsRow } from './useTotalsRow';
import { VF } from '@rd-web-markets/shared/dist/models/ParamValidator';
import { useSelector } from 'react-redux';
const tableSettingsForCostFragmentType = {
  'UkSection::ClaimEmployee': employeeTableSettings,
  'UkSection::ClaimSubcontractor': subcontractorTableSettings,
  'UkSection::ClaimExternalWorker': externalWorkerTableSettings,
  'UkSection::ClaimConsumable': consumableTableSettings,
  'UkSection::ClaimSoftwareConsumable': softwareConsumableTableSettings,
  'UkSection::CloudComputing': cloudComputingTableSettings,
  'UkSection::DataLicencesCost': dataLicenseCostTableSettings,
  'UkSection::IndependentRndContribution': independentRndContributionCostTableSettings,
  'UkSection::PaymentsToClinicalTrial': paymentsToClinicalTrialCostTableSettings
};
export default function ReactDataGridCostFragmentsTable(_ref) {
  var _claim$company$busine;
  let {
    claim,
    costFragmentType,
    userUnauthorizedToEdit,
    refreshClaim,
    allProjects,
    projectsLoading
  } = _ref;
  const [costFragments, setCostFragments, refreshCostFragments, totals, setTotals] = costFragmentsService.useGetAll(claim.id, costFragmentType);
  const fetchChildCostFragments = costFragmentsService.useFetchChildCostFragments(claim.id, costFragmentType);
  const user = useSelector(state => state.auth.user);
  const [editingDropdownCell, setEditingDropdownCell] = useState(null); // { rowId, fieldName }

  const deleteCostFragment = costFragmentsService.useDelete((costFragmentId, totals) => {
    const indexInStateArray = costFragments.findIndex(costFragment => costFragment.id === costFragmentId);
    costFragments.splice(indexInStateArray, 1);
    setCostFragments([...costFragments]);
    setTotals(totals);
  });

  // Receives a map of updated cost fragment rows and sets cost fragments
  const updateCostFragmentsState = updatedRowsMap => {
    setCostFragments(currentFragments => {
      return currentFragments.map(fragment => updatedRowsMap.has(fragment.id) ? updatedRowsMap.get(fragment.id) : fragment);
    });
  };
  const {
    staffing_cost_type,
    cost_methodology
  } = claim.claim_group;
  const tableSettings = tableSettingsForCostFragmentType[costFragmentType];

  // Get all child cost fragments for a given cost fragment
  const getChildRows = useCallback(row => {
    return costFragments.filter(cf => cf.parent_id === row.id && cf.is_expanded_row_child_row);
  }, [costFragments]);

  // When expanding a row - fetch all child cost fragments
  const fetchChildCostFragmentsForExpandedRow = useCallback(async row => {
    const existingChildren = getChildRows(row);
    if (existingChildren.length > 0) {
      return existingChildren;
    }
    const childCostFragments = await fetchChildCostFragments(row.claim_project_report_id);
    const markedChildren = childCostFragments.map(child => _objectSpread(_objectSpread({}, child), {}, {
      is_expanded_row_child_row: true,
      parent_id: row.id
    }));
    setCostFragments(currentFragments => [...currentFragments, ...markedChildren]);
    return markedChildren;
  }, [getChildRows, fetchChildCostFragments, setCostFragments]);

  // Only rows for commercial projects are expandable
  const isRowExpandable = useCallback(row => {
    var _row$claim_project_re;
    return ((_row$claim_project_re = row.claim_project_report) === null || _row$claim_project_re === void 0 ? void 0 : _row$claim_project_re.program_type) === 'program_commercial_project';
  }, []);

  // Callback to notify when ClaimProjectReportDropdown2 starts/stops editing
  const handleDropdownEditingChange = useCallback((rowId, fieldName, isEditing) => {
    setEditingDropdownCell(isEditing ? {
      rowId,
      fieldName
    } : null);
  }, []);

  // Dynamic row height - increase height when editing ClaimProjectReportDropdown2
  const getRowHeight = useCallback(row => {
    if (editingDropdownCell && row.id === editingDropdownCell.rowId) {
      return 200; // Increased height for dropdown editing
    }
    return 35; // Default row height
  }, [editingDropdownCell]);
  const isDeleteRowDisabled = useCallback(row => {
    return claim.is_locked || claim.financials_locked || claim.active_change_set || VF.isNumber(row.id) && row.id < 0;
  }, [claim.is_locked, claim.financials_locked, claim.active_change_set]);
  const deleteRowTooltipText = useCallback(row => {
    if (VF.isNumber(row.id) && row.id < 0) {
      return 'This placeholder commercial project row cannot be deleted';
    }
    if (claim.is_locked) {
      return 'Claim is locked.';
    }
    if (claim.financials_locked) {
      return 'Financials are locked.';
    }
    if (claim.active_change_set) {
      return 'Change is under review';
    }
    return null;
  }, [claim.active_change_set, claim.financials_locked, claim.is_locked]);

  // Build mapping of column values for the totals row - necessary because the values dont match other rows
  const totalsRowMapping = useTotalsRow(totals);
  const headers = useMemo(() => {
    return tableSettings.headers[cost_methodology][staffing_cost_type](claim).filter(Boolean);
  }, [tableSettings, staffing_cost_type, cost_methodology, claim]);

  // Build headers with edit cell logic included
  // This is only necessary because for UK Cost Fragments - its too custom. CommercialProjectToggle cells - are not editable, but they are clickable.
  // Some columns are not editable - but they differ between each cost fragment's project's type.
  const headersWithRenderers = useMemo(() => {
    return headers.map((header, index) => {
      const baseEditable = header.cellType && !header.disabled && header.cellType !== 'CommercialProjectToggle';
      // Provide renderEditCell for any cell that has a cellType (except CommercialProjectToggle)
      // This allows the editable function to override disabled state for special cases
      const shouldProvideEditCell = header.cellType && header.cellType !== 'CommercialProjectToggle';

      // Dynamically increase width if this column is being edited with ClaimProjectReportDropdown2
      const isEditingDropdownInThisColumn = editingDropdownCell && editingDropdownCell.fieldName === header.fieldName;
      return _objectSpread(_objectSpread(_objectSpread({}, header), isEditingDropdownInThisColumn && {
        width: 400,
        minWidth: 400
      }), {}, {
        editable: row => {
          var _row$claim_project_re2;
          /**
           * This is a hack - until business desides how this table should work. Initially we displayed cost fragments that existed in the database.
           * Then we decided that projects that are connected to commercial projects - should appear only when the cost fragment for the commercial project is expanded.
           * However if the commercial project does not itself has cost fragments - then it wont appear. And it is impossible to expand it, so the cost fragments for 
           * connected projects also wont be visible.
           * At the same time we cannot simply create a cost fragment every time we create a commercial project - because it is not yet decided
           * how this should work either, and simply doing it would make the data rubbish.
           * Once we decide what we should do with these costs - we should fix this hack.
           * These dummy rows - MUST NOT be editable.
           */
          if (VF.isNumber(row.id) && row.id < 0) {
            return false;
          }

          // Check for child rows with special project types
          const programType = (_row$claim_project_re2 = row.claim_project_report) === null || _row$claim_project_re2 === void 0 ? void 0 : _row$claim_project_re2.program_type;

          // For "included cost" child rows, only eligible_rnd_percentage is editable.
          // Even though it is disabled for parent commercial project rows.
          if (programType === 'connected_to_program_commercial_included_cost') {
            return header.fieldName === 'eligible_rnd_percentage' || header.cellType === 'ProjectSelector';
          }
          if (row.isTotalsRow) return false;
          if (!baseEditable) return false;

          // Default: editable if baseEditable is true
          return true;
        },
        renderEditCell: shouldProvideEditCell ? _ref2 => {
          let {
            row
          } = _ref2;
          return /*#__PURE__*/React.createElement("div", {
            style: {
              padding: '4px',
              width: '100%',
              height: '100%'
            }
          }, /*#__PURE__*/React.createElement(CostFragmentCell, {
            claim: claim,
            refreshClaim: refreshClaim,
            costFragments: costFragments,
            setCostFragments: setCostFragments,
            refreshCostFragments: refreshCostFragments,
            costFragmentType: costFragmentType,
            cellType: header.cellType,
            fieldName: header.fieldName,
            costFragment: row,
            model: header.model,
            triggerFragmentUpdate: header.triggerFragmentUpdate,
            userUnauthorizedToEdit: userUnauthorizedToEdit,
            disabled: header.disabled,
            translateValue: header.translateValue,
            allProjects: allProjects,
            setTotals: setTotals,
            onDropdownEditingChange: handleDropdownEditingChange
          }));
        } : undefined
      });
    });
  }, [headers, claim, refreshClaim, costFragments, setCostFragments, refreshCostFragments, costFragmentType, userUnauthorizedToEdit, allProjects, setTotals, handleDropdownEditingChange, editingDropdownCell]);
  const rows = useMemo(() => {
    if (!costFragments) {
      return [];
    }

    // Only include parent fragments (not children) - children will be inserted by expansion hook
    const parentFragments = costFragments.filter(cf => !cf.is_expanded_row_child_row);
    return parentFragments;
  }, [costFragments]);
  if (!staffing_cost_type || !cost_methodology) {
    return /*#__PURE__*/React.createElement(CardErrorMessage, {
      errorMessage: 'Staffig cost or cost methodology missing for claim group.'
    });
  }
  if (!tableSettings) {
    return /*#__PURE__*/React.createElement(CardErrorMessage, {
      errorMessage: "No cost fragment table settings for cost fragment type - ".concat(costFragmentType, ".")
    });
  }

  // if (!allProjects?.length && !projectsLoading) {
  //   return <CardErrorMessage errorMessage={'Please add project reports to this claim group first, before accessing this section.'} />
  // }

  if (!((_claim$company$busine = claim.company.business_units) !== null && _claim$company$busine !== void 0 && _claim$company$busine.length)) {
    return /*#__PURE__*/React.createElement(CardErrorMessage, {
      errorMessage: 'Please add business units first, before accessing this section.'
    });
  }
  if (!costFragments) {
    return /*#__PURE__*/React.createElement(Loading, null);
  }
  const addNewRow = () => {
    setCostFragments([{
      id: "new_".concat(costFragments.length + 1),
      claim_id: claim.id,
      fragmentable_type: costFragmentType,
      rate_type: claim.claim_group.rate_type,
      fragmentable: {},
      business_unit: claim.company.business_units[0],
      claim_project_report: allProjects[0],
      claim_project_report_id: allProjects[0].id,
      business_unit_id: claim.company.business_units[0].id
    }, ...costFragments]);
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("style", null, "\n      .cost-fragment-toggle-row-child {\n        height: 100%;\n        width: 50%;\n        margin-left: 50%;\n        border-left: 1px solid black;\n      }\n\n      .cost-fragment-toggle-row-child-content {\n        height: 1px;\n        position: relative;\n        top: 50%;\n        border-bottom: 1px solid black;\n      }\n\n      .Table-Cell-Content-expanded-last .cost-fragment-toggle-row-child {\n        height: 50%;\n        border-bottom: 1px solid black;\n      }\n\n      .Table-Cell-Content-expanded-last .cost-fragment-toggle-row-child-content {\n        display: none;\n      }\n\n      .CostFragment-DataGrid-ClaimProjectReportDropdown .list-group {\n        max-height: 120px;\n        overflow: scroll;\n      }\n    "), /*#__PURE__*/React.createElement(DataGridTable, {
    headers: headersWithRenderers,
    rows: rows,
    totalsRow: totalsRowMapping,
    onRowsChange: updateCostFragmentsState,
    onRowExpand: fetchChildCostFragmentsForExpandedRow,
    getChildRows: getChildRows,
    isRowExpandable: isRowExpandable,
    rowHeight: getRowHeight,
    showAddRowButton: user.account_type !== 'customer',
    onAddRow: addNewRow,
    addRowButtonDisabled: claim.is_locked || claim.financials_locked || claim.active_change_set,
    showLoadingAlert: projectsLoading,
    showDeleteColumn: true,
    onDeleteRow: deleteCostFragment,
    deleteRowDisabled: isDeleteRowDisabled,
    deleteRowTooltipText: deleteRowTooltipText
  }));
}